import { Component, OnInit, Input, Output, EventEmitter, inject } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { MedicationGroup } from '../../models/medication-group';
import { NursingAuditService } from 'src/app/services/nursing-audit.service';
import { AuthService } from 'src/app/services/auth.service';
import { concatMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-medication-details',
  templateUrl: './medication-details.component.html',
  styleUrls: ['./medication-details.component.scss'],
  standalone: false
})
export class MedicationDetailsComponent implements OnInit {
  private readonly auditService = inject(NursingAuditService);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);

  assayManagementEnabled = environment.enableAssayManagement;
  @Input() medication: MedicationGroup | null = null;
  @Input() canEdit = true;
  @Output() back = new EventEmitter<void>();

  breadcrumbItems: MenuItem[] = [];

  selectedWeight: number | null = null;
  assayTrackable = false;
  originalWeight: number | null = null;
  originalAssay = false;

  showConfirmDialog = false;
  mappedItems: { description: string; medId: any; source: string }[] = [];
  ngOnInit(): void {
    this.breadcrumbItems = [
      { label: 'Monitored Items', command: () => this.back.emit() },
      { label: 'Item Group Details' }
    ];
    if (this.medication) {
      this.selectedWeight = this.medication.diversionWeight;
      this.assayTrackable = !!this.medication.assayTrackable;
      this.originalWeight = this.selectedWeight;
      this.originalAssay = this.assayTrackable;
      this.buildMappedItems();
    }
  }

  buildMappedItems(): void {
    const mappedItems = this.medication?.mappedItems || [];
    if (mappedItems.length) {
      this.mappedItems = mappedItems
        .map(item => {
          let medId = null;
          let source = '';

          if (item.adcMedId != null && String(item.adcMedId).trim() !== '') {
            medId = item.adcMedId;
            source = 'ADC';
          } else if (item.ehrMedId != null && String(item.ehrMedId).trim() !== '') {
            medId = item.ehrMedId;
            source = 'EHR';
          }

          return { description: item.description, medId, source };
        })
        .filter(item => item.medId !== null);
      return;
    }

    const descriptions = this.medication?.medDescription || [];
    const adcIds = this.medication?.adcMedId || [];
    const ehrIds = this.medication?.ehrMedId || [];

    this.mappedItems = descriptions.map((desc, i) => {
      let medId = null;
      let source = '';

      if (adcIds[i] != null && String(adcIds[i]).trim() !== '') {
        medId = adcIds[i];
        source = 'ADC';
      } else if (ehrIds[i] != null && String(ehrIds[i]).trim() !== '') {
        medId = ehrIds[i];
        source = 'EHR';
      }

      return { description: desc, medId, source };
    }).filter(item => item.medId !== null);
  }

  get isDirty(): boolean {
    return this.selectedWeight !== this.originalWeight || this.assayTrackable !== this.originalAssay;
  }

  onWeightChange(weight: number): void {
    this.selectedWeight = weight;
  }

  save(): void {
    if (!this.isDirty) return;
    this.showConfirmDialog = true;
  }

  confirmSave(): void {
    if (!this.canEdit) {
      this.showConfirmDialog = false;
      return;
    }

    this.saveMedicationGroup();
  }

  private saveMedicationGroup(): void {
    if (!this.medication) {
      return;
    }

    this.showConfirmDialog = false;
    const payload = {
      medGroupName: this.medication.medGroupName,
      diversionWeight: this.selectedWeight,
      assayTrackable: this.assayTrackable,
      userRole: this.authService.user?.role || ''
    };
    this.auditService.saveMedicationGroup(payload).pipe(
      concatMap(() => this.auditService.saveItemsMonitorHistory(this.buildItemsMonitorHistoryPayload()))
    ).subscribe({
      next: () => {
        this.originalWeight = this.selectedWeight;
        this.originalAssay = this.assayTrackable;
        this.messageService.add({ severity: 'success', summary: 'Changes saved' });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Failed to save changes' });
      }
    });
  }

  private buildItemsMonitorHistoryPayload(): any {
    const currentUser = this.authService.user || {};
    return {
      user: currentUser.fullname || '',
      userId: currentUser._id || currentUser.id || '',
      ChangedAt: new Date(),
      medId: this.getMedId(this.medication),
      medGroupName: this.medication?.medGroupName,
      diversionWeight: this.selectedWeight,
      assayTrackable: this.assayTrackable,
      monitor: this.medication?.monitor,
    };
  }

  private getMedId(medication: MedicationGroup | null): any {
    const mappedItem = medication?.mappedItems?.find(item =>
      this.hasMedId(item.adcMedId) || this.hasMedId(item.ehrMedId)
    );
    if (mappedItem) {
      return this.hasMedId(mappedItem.adcMedId) ? mappedItem.adcMedId : mappedItem.ehrMedId;
    }

    const adcMedId = medication?.adcMedId?.find(id => this.hasMedId(id));
    if (this.hasMedId(adcMedId)) {
      return adcMedId;
    }

    const ehrMedId = medication?.ehrMedId?.find(id => this.hasMedId(id));
    return this.hasMedId(ehrMedId) ? ehrMedId : '';
  }

  private hasMedId(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  cancelConfirm(): void {
    this.showConfirmDialog = false;
  }

  cancel(): void {
    this.back.emit();
  }

  onBreadcrumbClick(event: any): void {
    if (event.item?.command) {
      event.item.command();
    }
  }
}
